import { useState, useMemo, useEffect } from "react";
import { Modal } from "./Modal";
import { ActionButton, GhostButton, FormField, inputClass } from "./Primitives";
import { extractPlaceholders, applyPlaceholders, slotLabel } from "../lib/darQuickPhrases";

/**
 * Fill-in modal for a DAR template with {slot} placeholders. When the guard
 * presses "Insert", `onInsert(resolved_text)` is called with the final
 * substituted sentence.
 *
 * If the template has NO placeholders, the caller should short-circuit and
 * insert directly — this modal isn't rendered.
 */
export default function DarPhraseFillModal({ template, initialValues, onClose, onInsert }) {
  const slots = useMemo(() => extractPlaceholders(template), [template]);
  const [values, setValues] = useState({});

  // Seed with per-slot defaults (from the caller's context — selected shift,
  // current time, etc.) whenever the underlying template changes.
  useEffect(() => {
    const seed = {};
    slots.forEach((s) => {
      const v = initialValues?.[s];
      if (v != null && v !== "") seed[s] = String(v);
    });
    setValues(seed);
  }, [template, slots, initialValues]);

  const preview = useMemo(() => applyPlaceholders(template, values), [template, values]);
  const allFilled = slots.every((s) => (values[s] || "").trim().length > 0);

  const insert = () => {
    if (!allFilled) return;
    onInsert(preview);
    onClose();
  };

  return (
    <Modal onClose={onClose} title="Fill in the phrase" testId="dar-phrase-fill-modal">
      <div className="space-y-4">
        <div className="text-xs text-zinc-500 uppercase tracking-widest">Template</div>
        <div className="p-3 bg-[#0A0A0A] border border-[#27272A] text-sm text-zinc-300 whitespace-pre-wrap font-mono">
          {template}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {slots.map((s) => (
            <FormField key={s} label={slotLabel(s)}>
              <input
                data-testid={`dar-slot-${s}`}
                className={inputClass}
                value={values[s] || ""}
                onChange={(e) => setValues((v) => ({ ...v, [s]: e.target.value }))}
                autoFocus={s === slots[0]}
                placeholder={`e.g. …`}
              />
            </FormField>
          ))}
        </div>

        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest mb-1.5">Preview</div>
          <div
            data-testid="dar-phrase-preview"
            className={`p-3 border text-sm whitespace-pre-wrap ${
              allFilled
                ? "bg-[#0A1F0F] border-[#22C55E]/40 text-zinc-100"
                : "bg-[#0A0A0A] border-[#27272A] text-zinc-500"
            }`}
          >
            {preview}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <ActionButton
            data-testid="dar-phrase-insert"
            onClick={insert}
            disabled={!allFilled}
            className={!allFilled ? "opacity-50 cursor-not-allowed" : ""}
          >
            Insert into DAR
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}
